/**
 * Cross-join CTA cards for parents to enable the *other* PlayFirst app.
 *
 *  - PFP-only parent → shows "Connect with my speech therapist" (email OR code)
 *  - Clinical-only parent → shows "Turn on PlayFirst Parent"
 *
 * Feb 2026: `ConnectTherapistCard` now surfaces a "Send again?" dialog when
 * the backend rejects the connect with 409 — instead of a dead-end toast.
 * The resend hits POST /parent-app/therapist-links/{link_id}/resend, which
 * re-fires the weekly magic-link email for auto_therapist_lite links, or
 * re-pings the in-app notification for full-therapist pending links.
 */
import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Stethoscope, Heart, ArrowRight, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export function ConnectTherapistCard() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('email'); // 'email' | 'code'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  // Resend dialog state — surfaced when POST /therapist-connect returns 409.
  const [resendState, setResendState] = useState(null); // {email, link, createdAt}

  /**
   * When the parent hits 409, we look up their existing link with this
   * therapist (they may have several — pick the most recent one on that
   * email) and open the AlertDialog so they can re-send.
   */
  const findExistingLink = async (targetEmail) => {
    try {
      const { data } = await axios.get(`${API}/parent-app/therapist-links`, authHeaders());
      const norm = (targetEmail || '').trim().toLowerCase();
      const rows = (data.links || [])
        .filter((l) => (l.therapist_email || '').toLowerCase() === norm)
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      return rows[0] || null;
    } catch {
      return null;
    }
  };

  const submit = async () => {
    if (mode === 'email' && !email.trim()) return toast.error("Enter your therapist's email");
    if (mode === 'code' && !code.trim()) return toast.error('Enter the invite code');
    setSubmitting(true);
    try {
      const payload = mode === 'email'
        ? { therapist_email: email.trim().toLowerCase() }
        : { invite_code: code.trim().toUpperCase() };
      await axios.post(`${API}/parent-app/therapist-connect`, payload, authHeaders());
      setDone(true);
      toast.success('Request sent — your therapist will approve shortly');
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || 'Could not send request';
      if (status === 409 && mode === 'email') {
        const existing = await findExistingLink(email.trim().toLowerCase());
        setResendState({
          email: email.trim().toLowerCase(),
          link: existing,
        });
        setOpen(false);
      } else {
        toast.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const doResend = async () => {
    if (!resendState?.link?.id) {
      toast.info('That invite could not be located. Please try again shortly.');
      setResendState(null);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/parent-app/therapist-links/${resendState.link.id}/resend`,
        {},
        authHeaders(),
      );
      if (data.email_sent) {
        if (data.channel === 'therapist_lite_weekly') {
          toast.success(`Fresh magic-link sent to ${resendState.email}`);
        } else {
          toast.success(`Nudged ${resendState.email} — they'll see the request in PlayFirst.`);
        }
      } else {
        toast.warning('Recorded — but the email service didn\'t confirm delivery. Try again in a few minutes.');
      }
      setResendState(null);
      setEmail('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not resend');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Card
        className="p-5 md:p-6 rounded-3xl border border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5"
        data-testid="connect-therapist-card"
      >
        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Stethoscope className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-serif text-primary-900 mb-1">
                Connect with my speech therapist
              </h3>
              <p className="text-sm text-stone-600 max-w-md">
                Your therapist can share a Language Map, push activities into your
                daily play ideas, and check in on progress — all in one place.
              </p>
            </div>
          </div>
          <Button
            onClick={() => setOpen(true)}
            className="rounded-full whitespace-nowrap"
            data-testid="connect-therapist-btn"
          >
            Get connected
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setDone(false); setEmail(''); setCode(''); } }}>
        <DialogContent className="rounded-3xl max-w-md" data-testid="connect-therapist-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">
              {done ? 'Request sent' : 'Connect with your therapist'}
            </DialogTitle>
          </DialogHeader>
          {done ? (
            <div className="space-y-3 mt-2">
              <p className="text-sm text-stone-600">
                Your therapist will see your request. If they use PlayFirst, they'll approve it
                in their dashboard. If they don't, they'll receive a weekly link to set your
                child's PFP goals. You'll get an in-app notification when they're connected —
                usually within a day.
              </p>
              <Button className="w-full rounded-full" onClick={() => setOpen(false)}>
                Great, thanks
              </Button>
            </div>
          ) : (
            <>
              <div className="flex gap-2 mt-2 mb-1">
                <Button
                  variant={mode === 'email' ? 'default' : 'outline'}
                  size="sm"
                  className="rounded-full flex-1"
                  onClick={() => setMode('email')}
                  data-testid="mode-email"
                >
                  By email
                </Button>
                <Button
                  variant={mode === 'code' ? 'default' : 'outline'}
                  size="sm"
                  className="rounded-full flex-1"
                  onClick={() => setMode('code')}
                  data-testid="mode-code"
                >
                  By invite code
                </Button>
              </div>
              {mode === 'email' ? (
                <div className="space-y-2 mt-2">
                  <Label>Your therapist's email</Label>
                  <Input
                    type="email"
                    placeholder="e.g. therapist@practice.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 rounded-xl"
                    data-testid="therapist-email-input"
                  />
                  <p className="text-xs text-stone-400">
                    Any email — if they don't use PlayFirst, they'll get a weekly link to set goals.
                  </p>
                </div>
              ) : (
                <div className="space-y-2 mt-2">
                  <Label>Invite code from your school / practice</Label>
                  <Input
                    placeholder="e.g. ABC123"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    className="h-12 rounded-xl font-mono uppercase"
                    data-testid="invite-code-input"
                  />
                  <p className="text-xs text-stone-400">
                    Ask your therapist or school admin for the code.
                  </p>
                </div>
              )}
              <DialogFooter className="mt-4">
                <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  className="rounded-full"
                  onClick={submit}
                  disabled={submitting}
                  data-testid="send-connect-request"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Send request
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Feb 2026 — Resend dialog surfaced on 409 duplicate */}
      <AlertDialog open={!!resendState} onOpenChange={(v) => !v && setResendState(null)}>
        <AlertDialogContent data-testid="parent-resend-invite-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Invite already sent</AlertDialogTitle>
            <AlertDialogDescription>
              {resendState?.link?.created_at
                ? `You already invited ${resendState.email} on ${new Date(resendState.link.created_at).toLocaleDateString()}.`
                : `An invite to ${resendState?.email} already exists.`}
              {' '}Send it again? Useful if they didn't receive the first email (spam / typo / just needs a nudge).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="parent-resend-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doResend}
              disabled={submitting}
              className="bg-primary hover:bg-primary/90"
              data-testid="parent-resend-confirm"
            >
              {submitting ? 'Sending…' : 'Send again'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function LearnAboutPfpCard() {
  const navigate = useNavigate();
  return (
    <Card
      className="p-5 md:p-6 rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-teal-50"
      data-testid="learn-about-pfp-card"
    >
      <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <Heart className="w-6 h-6 text-emerald-700" />
          </div>
          <div>
            <h3 className="text-lg font-serif text-emerald-900 mb-1">
              Turn on PlayFirst Parent — support at home
            </h3>
            <p className="text-sm text-emerald-800 max-w-md">
              A gentle daily play idea, weekly summary, and monthly milestones —
              on WhatsApp. Neurodiversity-affirming and GLP-informed.
            </p>
          </div>
        </div>
        <Button
          onClick={() => navigate('/parent-app/pricing')}
          className="rounded-full bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap"
          data-testid="learn-about-pfp-btn"
        >
          Learn more
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </Card>
  );
}
